
import { useCallback, useRef } from 'react';
import { 
  UnifiedStreamingService, 
  AiStreamingRequest, 
  StreamingStartEvent, 
  StreamingCompleteEvent 
} from '@/services/UnifiedStreamingService';

interface UseAiServiceManagementProps {
  userId: string | null;
  language: string;
  onAiServiceError: (error: Error, requestId: string) => void;
  onStreamingStart: (data: StreamingStartEvent) => void;
  onStreamingContent: (chunk: string, fullContent: string, requestId?: string) => void;
  onStreamingComplete: (data: StreamingCompleteEvent) => void;
  onStreamingError: (error: string, requestId?: string) => void;
}

interface UseAiServiceManagementReturn {
  processAiRequest: (request: any) => Promise<any>;
  stopStreaming: (requestId: string) => void;
  stopAllStreaming: () => void;
  isStreaming: (requestId: string) => boolean;
}

export const useAiServiceManagement = ({
  userId,
  language,
  onAiServiceError,
  onStreamingStart,
  onStreamingContent,
  onStreamingComplete,
  onStreamingError
}: UseAiServiceManagementProps): UseAiServiceManagementReturn => {
  
  const activeRequestsRef = useRef<Set<string>>(new Set());

  const processAiRequest = useCallback(async (request: any) => {
    const requestId = request.requestId || crypto.randomUUID();
    
    // Reduced logging for performance
    if (process.env.NODE_ENV === 'development') {
      console.log('[AI SERVICE OPTIMIZED] Processing request:', {
        requestId,
        messagesCount: Array.isArray(request.messages) ? request.messages.length : 0
      });
    }

    if (!userId) {
      const error = new Error('User ID is required for AI requests');
      onAiServiceError(error, requestId);
      return null;
    }

    if (!Array.isArray(request.messages) || request.messages.length === 0) {
      const error = new Error('Conversation history is required for AI requests');
      onAiServiceError(error, requestId);
      return null;
    }

    activeRequestsRef.current.add(requestId);
    
    try {
      const streamingRequest: AiStreamingRequest = {
        conversationHistory: request.messages,
        topic: request.topic || 'guide',
        sessionId: request.sessionId || '',
        userId,
        language,
        requestId,
        includeMultiLanguage: request.includeMultiLanguage || false,
        mode: request.mode || 'conversation'
      };

      // Simplified callback handling - no wrapper overhead
      const result = await UnifiedStreamingService.startStreaming(
        streamingRequest, 
        {
          onStart: onStreamingStart,
          onContent: onStreamingContent,
          onComplete: onStreamingComplete,
          onError: onStreamingError
        }
      );

      if (process.env.NODE_ENV === 'development') {
        console.log('[AI SERVICE OPTIMIZED] Request completed:', {
          requestId,
          success: result.success
        });
      }

      return result;

    } catch (error) {
      const aiError = error instanceof Error ? error : new Error(String(error));
      onAiServiceError(aiError, requestId);
      onStreamingError(aiError.message, requestId);
      
      return {
        success: false,
        error: aiError.message,
        requestId
      };
    } finally {
      activeRequestsRef.current.delete(requestId);
    }
  }, [userId, language, onAiServiceError, onStreamingStart, onStreamingContent, onStreamingComplete, onStreamingError]);

  const stopStreaming = useCallback((requestId: string) => {
    UnifiedStreamingService.stopStreaming(requestId);
    activeRequestsRef.current.delete(requestId);
  }, []);

  const stopAllStreaming = useCallback(() => {
    UnifiedStreamingService.stopAllStreaming();
    activeRequestsRef.current.clear();
  }, []);

  const isStreaming = useCallback((requestId: string) => {
    return UnifiedStreamingService.isStreaming(requestId) || activeRequestsRef.current.has(requestId);
  }, []);

  return {
    processAiRequest,
    stopStreaming,
    stopAllStreaming,
    isStreaming
  };
};
